<script>
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers } from '@vuelidate/validators';
import { mapGetters } from 'vuex';

import HSMTemplateHeader from './hsm-templates/components/HSMTemplateHeader.vue';
import HSMTemplateFilters from './hsm-templates/components/HSMTemplateFilters.vue';
import HSMTemplateList from './hsm-templates/components/HSMTemplateList.vue';
import HSMTemplateDetails from './hsm-templates/components/HSMTemplateDetails.vue';
import HSMTemplateForm from './hsm-templates/components/HSMTemplateForm.vue';

const templateNameValidator = helpers.regex(/^[a-z0-9_]+$/);

// Meta API error code mapping (error_code and error_subcode)
const META_ERROR_CODE_MAP = {
  // Main error codes
  190: 'PERMISSION_DENIED', // Access token expired or invalid
  4: 'RATE_LIMIT_EXCEEDED', // Rate limit exceeded
  368: 'PERMISSION_DENIED', // Permission denied
  // Template-specific error subcodes
  2388023: 'DUPLICATE_NAME', // Template name already exists
  2388024: 'DUPLICATE_NAME', // Template with same language already exists
  2388025: 'INVALID_NAME_FORMAT', // Invalid template name format
  2388030: 'BODY_TOO_LONG', // Body text too long
  2388031: 'HEADER_TOO_LONG', // Header text too long
  2388032: 'FOOTER_TOO_LONG', // Footer text too long
  2388033: 'TOO_MANY_BUTTONS', // Too many buttons
  2388034: 'BUTTON_TEXT_TOO_LONG', // Button text too long
  2388035: 'MISSING_VARIABLE_EXAMPLES', // Missing variable examples
  // Note: error_code 100 is generic "Invalid parameter", we rely on message patterns instead
};

// Keywords to detect in error messages
const ERROR_MESSAGE_PATTERNS = [
  { pattern: /invalid.*(parameter|value)/i, key: 'INVALID_PARAMETER' },
  { pattern: /duplicate|already exists/i, key: 'DUPLICATE_NAME' },
  { pattern: /name.*(length|long|characters)/i, key: 'NAME_TOO_LONG' },
  { pattern: /body.*(length|long|characters)/i, key: 'BODY_TOO_LONG' },
  { pattern: /header.*(length|long|characters)/i, key: 'HEADER_TOO_LONG' },
  { pattern: /footer.*(length|long|characters)/i, key: 'FOOTER_TOO_LONG' },
  { pattern: /button.*(length|long|characters)/i, key: 'BUTTON_TEXT_TOO_LONG' },
  { pattern: /too many buttons/i, key: 'TOO_MANY_BUTTONS' },
  { pattern: /example|sample/i, key: 'MISSING_VARIABLE_EXAMPLES' },
  { pattern: /rate.?limit|throttl/i, key: 'RATE_LIMIT_EXCEEDED' },
  {
    pattern: /permission|access|denied|unauthorized/i,
    key: 'PERMISSION_DENIED',
  },
  { pattern: /media.*required|missing.*media/i, key: 'MEDIA_REQUIRED' },
  { pattern: /media.*(format|type)|unsupported/i, key: 'INVALID_MEDIA_FORMAT' },
];

export default {
  components: {
    HSMTemplateHeader,
    HSMTemplateFilters,
    HSMTemplateList,
    HSMTemplateDetails,
    HSMTemplateForm,
  },
  props: {
    inbox: {
      type: Object,
      required: true,
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      showCreateModal: false,
      showEditModal: false,
      showDeleteConfirm: false,
      templateToDelete: null,
      selectedTemplate: null,
      searchQuery: '',
      filterStatus: '',
      filterLanguage: '',
      filterCategory: '',
      filtersCollapsed: false,
      // Form fields
      name: '',
      category: 'MARKETING',
      language: 'pt_BR',
      header: '',
      footer: '',
      mediaType: '',
      body: '',
      buttons: [],
      bodyVariableExamples: [],
      file: null,
    };
  },
  validations() {
    return {
      name: {
        required,
        templateNameValidator: helpers.withMessage(
          this.$t('INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.CREATE.NAME_HELP'),
          templateNameValidator
        ),
      },
      body: { required },
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'inboxes/getUIFlags',
    }),
    templates() {
      const templates = this.inbox.message_templates;
      return Array.isArray(templates) ? templates : [];
    },
    filteredTemplates() {
      return this.templates.filter(template => {
        const matchesSearch =
          !this.searchQuery ||
          template.name?.toLowerCase().includes(this.searchQuery.toLowerCase());
        const matchesStatus =
          !this.filterStatus || template.status === this.filterStatus;
        const matchesLanguage =
          !this.filterLanguage || template.language === this.filterLanguage;
        const matchesCategory =
          !this.filterCategory || template.category === this.filterCategory;
        return (
          matchesSearch && matchesStatus && matchesLanguage && matchesCategory
        );
      });
    },
    availableStatuses() {
      const statuses = [...new Set(this.templates.map(t => t.status))];
      return statuses.filter(Boolean);
    },
    availableLanguages() {
      const languages = [...new Set(this.templates.map(t => t.language))];
      return languages.filter(Boolean);
    },
    availableCategories() {
      const categories = [...new Set(this.templates.map(t => t.category))];
      return categories.filter(Boolean);
    },
    detectedVariables() {
      const matches = this.body.match(/\{\{(\d+)\}\}/g) || [];
      return [...new Set(matches)].sort((a, b) => {
        const numA = parseInt(a.match(/\d+/)[0], 10);
        const numB = parseInt(b.match(/\d+/)[0], 10);
        return numA - numB;
      });
    },
    categoryOptions() {
      return [
        {
          value: 'MARKETING',
          label: this.$t(
            'INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.CATEGORIES.MARKETING'
          ),
        },
        {
          value: 'UTILITY',
          label: this.$t(
            'INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.CATEGORIES.UTILITY'
          ),
        },
      ];
    },
    languageOptions() {
      return [
        { value: 'pt_BR', label: 'Portugues (Brasil)' },
        { value: 'en_US', label: 'English (US)' },
        { value: 'es', label: 'Espanol' },
      ];
    },
    validButtons() {
      return this.buttons.filter(btn => btn && btn.trim());
    },
    isFormValid() {
      if (this.v$.$invalid) return false;
      if (this.detectedVariables.length > 0) {
        return this.detectedVariables.every(
          (_, index) =>
            this.bodyVariableExamples[index] &&
            this.bodyVariableExamples[index].trim()
        );
      }
      if (this.mediaType && this.mediaType !== 'TEXT' && !this.file)
        return false;
      return true;
    },
    deleteConfirmMessage() {
      return this.$t('INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.DELETE.CONFIRM', {
        name: this.templateToDelete?.name || '',
      });
    },
    previewTemplate() {
      const components = [];

      // Add HEADER component if header text or media type exists
      if (this.header || this.mediaType) {
        const headerComponent = { type: 'HEADER' };
        if (this.mediaType) {
          headerComponent.format = this.mediaType;
        }
        if (this.header) {
          headerComponent.format = 'TEXT';
          headerComponent.text = this.header;
        }
        components.push(headerComponent);
      }

      // Add BODY component
      components.push({ type: 'BODY', text: this.body });

      // Add FOOTER component if footer text exists
      if (this.footer) {
        components.push({ type: 'FOOTER', text: this.footer });
      }

      // Add BUTTONS component if buttons exist
      if (this.validButtons.length > 0) {
        components.push({
          type: 'BUTTONS',
          buttons: this.validButtons.map(text => ({
            type: 'QUICK_REPLY',
            text,
          })),
        });
      }

      return {
        name: this.name,
        category: this.category,
        language: this.language,
        components,
      };
    },
  },
  watch: {
    detectedVariables: {
      handler(newVars) {
        const newExamples = [];
        newVars.forEach((_, index) => {
          newExamples[index] = this.bodyVariableExamples[index] || '';
        });
        this.bodyVariableExamples = newExamples;
      },
      immediate: true,
    },
  },
  methods: {
    selectTemplate(template) {
      this.selectedTemplate = template;
    },
    openCreateModal() {
      this.resetForm();
      this.showCreateModal = true;
    },
    closeCreateModal() {
      this.showCreateModal = false;
      this.resetForm();
    },
    openEditModal(template) {
      this.selectedTemplate = template;
      this.name = template.name;
      this.category = template.category || 'MARKETING';
      this.language = template.language || 'pt_BR';
      this.body = this.getTemplateBody(template);

      // Load header
      const headerComponent = template.components?.find(
        c => c.type === 'HEADER'
      );
      if (headerComponent) {
        if (headerComponent.format === 'TEXT') {
          this.header = headerComponent.text || '';
          this.mediaType = '';
        } else {
          this.header = '';
          this.mediaType = headerComponent.format || '';
        }
      } else {
        this.header = '';
        this.mediaType = '';
      }

      // Load footer
      const footerComponent = template.components?.find(
        c => c.type === 'FOOTER'
      );
      this.footer = footerComponent?.text || '';

      // Load buttons
      const buttonsComponent = template.components?.find(
        c => c.type === 'BUTTONS'
      );
      this.buttons = buttonsComponent?.buttons?.map(b => b.text) || [];

      // Load body variable examples from existing template
      const bodyComponent = template.components?.find(c => c.type === 'BODY');
      const existingExamples = bodyComponent?.example?.body_text?.[0] || [];
      this.bodyVariableExamples = [...existingExamples];

      this.showEditModal = true;
    },
    closeEditModal() {
      this.showEditModal = false;
      this.resetForm();
    },
    getTemplateBody(template) {
      const bodyComponent = template.components?.find(c => c.type === 'BODY');
      return bodyComponent?.text || '';
    },
    resetForm() {
      this.name = '';
      this.category = 'MARKETING';
      this.language = 'pt_BR';
      this.header = '';
      this.footer = '';
      this.mediaType = '';
      this.body = '';
      this.buttons = [];
      this.bodyVariableExamples = [];
      this.file = null;
      this.v$.$reset();
    },
    addButton() {
      if (this.buttons.length < 10) {
        this.buttons.push('');
      }
    },
    removeButton(index) {
      this.buttons.splice(index, 1);
    },
    clearFilters() {
      this.searchQuery = '';
      this.filterStatus = '';
      this.filterLanguage = '';
      this.filterCategory = '';
    },
    async createTemplate() {
      this.v$.$touch();
      if (!this.isFormValid) {
        if (this.detectedVariables.length > 0) {
          const hasAllExamples = this.detectedVariables.every(
            (_, index) =>
              this.bodyVariableExamples[index] &&
              this.bodyVariableExamples[index].trim()
          );
          if (!hasAllExamples) {
            useAlert(
              this.$t(
                'INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.ERRORS.EXAMPLES_REQUIRED'
              )
            );
            return;
          }
        }
        return;
      }

      try {
        let templateData;
        if (this.file) {
          templateData = new FormData();
          templateData.append('name', this.name);
          templateData.append('category', this.category);
          templateData.append('language', this.language);
          templateData.append('header', this.header);
          templateData.append('footer', this.footer);
          templateData.append('media_type', this.mediaType);
          templateData.append('body', this.body);
          templateData.append('file', this.file);

          this.bodyVariableExamples
            .filter(v => v && v.trim())
            .forEach(v => templateData.append('body_variables[]', v));

          this.validButtons.forEach(b => templateData.append('buttons[]', b));
        } else {
          templateData = {
            name: this.name,
            category: this.category,
            language: this.language,
            header: this.header,
            footer: this.footer,
            media_type: this.mediaType,
            body: this.body,
            body_variables: this.bodyVariableExamples.filter(
              v => v && v.trim()
            ),
            buttons: this.validButtons,
          };
        }

        await this.$store.dispatch('inboxes/createHSMTemplate', {
          inboxId: this.inbox.id,
          templateData,
        });

        useAlert(
          this.$t('INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.CREATE.SUCCESS')
        );
        this.closeCreateModal();

        await this.$store.dispatch(
          'inboxes/syncWhatsappTemplates',
          this.inbox.id
        );
      } catch (error) {
        const errorMessage = this.getErrorMessage(error);
        useAlert(errorMessage);
      }
    },
    async updateTemplate() {
      this.v$.$touch();
      if (!this.isFormValid) return;

      try {
        const templateData = {
          name: this.name,
          category: this.category,
          language: this.language,
          header: this.header,
          footer: this.footer,
          media_type: this.mediaType,
          body: this.body,
          body_variables: this.bodyVariableExamples.filter(v => v && v.trim()),
          buttons: this.validButtons,
        };

        await this.$store.dispatch('inboxes/updateHSMTemplate', {
          inboxId: this.inbox.id,
          templateId: this.selectedTemplate.id,
          templateData,
        });

        useAlert(
          this.$t('INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.CREATE.SUCCESS')
        );
        this.closeEditModal();

        await this.$store.dispatch(
          'inboxes/syncWhatsappTemplates',
          this.inbox.id
        );
      } catch (error) {
        const errorMessage = this.getErrorMessage(error);
        useAlert(errorMessage);
      }
    },
    confirmDelete(template) {
      this.templateToDelete = template;
      this.showDeleteConfirm = true;
    },
    cancelDelete() {
      this.templateToDelete = null;
      this.showDeleteConfirm = false;
    },
    async deleteTemplate() {
      if (!this.templateToDelete) return;

      try {
        await this.$store.dispatch('inboxes/deleteHSMTemplate', {
          inboxId: this.inbox.id,
          templateName: this.templateToDelete.name,
        });

        useAlert(
          this.$t('INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.DELETE.SUCCESS')
        );
        this.cancelDelete();
        if (this.selectedTemplate?.name === this.templateToDelete?.name) {
          this.selectedTemplate = null;
        }

        await this.$store.dispatch(
          'inboxes/syncWhatsappTemplates',
          this.inbox.id
        );
      } catch (error) {
        const errorMessage = this.getErrorMessage(error);
        useAlert(errorMessage);
      }
    },
    async onSyncTemplates() {
      this.selectedTemplate = null;
      // Force refresh of inbox to ensure templates list is updated
      try {
        await this.$store.dispatch('inboxes/getInbox', this.inbox.id);
      } catch (error) {
        // Ignore error if fetch fails, sync might have worked partially or it's a transient issue
      }
    },
    /**
     * Process API error and return a user-friendly translated message
     * @param {Object} error - The error object from the API
     * @returns {string} - Translated error message
     */
    getErrorMessage(error) {
      const responseData = error?.response?.data;
      const errorCode = responseData?.error_code;
      const errorSubcode = responseData?.error_subcode;
      const errorDetails = responseData?.error_details;
      const errorMessage = responseData?.error || '';

      // Try to map by error subcode first (most specific)
      if (errorSubcode && META_ERROR_CODE_MAP[errorSubcode]) {
        const errorKey = META_ERROR_CODE_MAP[errorSubcode];
        return this.$t(
          `INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.ERRORS.${errorKey}`
        );
      }

      // Try to map by error code
      if (errorCode && META_ERROR_CODE_MAP[errorCode]) {
        const errorKey = META_ERROR_CODE_MAP[errorCode];
        return this.$t(
          `INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.ERRORS.${errorKey}`
        );
      }

      // Try to detect by message patterns
      const matchedPattern = ERROR_MESSAGE_PATTERNS.find(({ pattern }) =>
        pattern.test(errorMessage)
      );
      if (matchedPattern) {
        return this.$t(
          `INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.ERRORS.${matchedPattern.key}`
        );
      }

      // If we have error details from Meta API, show them (they are usually specific)
      if (errorDetails) {
        return errorDetails;
      }

      // If we have an error message from API but couldn't map it, show it
      // (backend now returns detailed messages from Meta API)
      if (errorMessage && errorMessage.toLowerCase() !== 'invalid parameter') {
        return errorMessage;
      }

      // For generic "Invalid parameter" or no message, show a helpful generic message
      if (
        errorMessage.toLowerCase().includes('invalid parameter') ||
        !errorMessage
      ) {
        return this.$t(
          'INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.ERRORS.INVALID_PARAMETER'
        );
      }

      // Fallback to generic error
      return this.$t(
        'INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.ERRORS.GENERIC_ERROR'
      );
    },
  },
};
</script>

<template>
  <div
    class="flex flex-col rounded-xl outline outline-1 outline-n-weak bg-n-solid-1 dark:bg-n-slate-1 overflow-hidden h-[600px] mb-8"
  >
    <!-- Header -->
    <HSMTemplateHeader
      :inbox="inbox"
      @create="openCreateModal"
      @sync="onSyncTemplates"
    />

    <!-- Main Content -->
    <div class="flex flex-1 overflow-hidden">
      <!-- Left Sidebar - Template List -->
      <div
        class="w-64 border-r border-n-weak flex flex-col bg-n-slate-2 dark:bg-n-slate-2"
      >
        <!-- Search and Filters -->
        <HSMTemplateFilters
          v-model:search-query="searchQuery"
          v-model:filter-status="filterStatus"
          v-model:filter-language="filterLanguage"
          v-model:filter-category="filterCategory"
          v-model:filters-collapsed="filtersCollapsed"
          :available-statuses="availableStatuses"
          :available-languages="availableLanguages"
          :available-categories="availableCategories"
          @clear-filters="clearFilters"
        />

        <!-- Template List -->
        <HSMTemplateList
          :templates="filteredTemplates"
          :selected-template="selectedTemplate"
          @select="selectTemplate"
          @edit="openEditModal"
          @create="openCreateModal"
        />
      </div>

      <!-- Right Panel - Template Details -->
      <HSMTemplateDetails
        :template="selectedTemplate"
        @edit="openEditModal"
        @delete="confirmDelete"
      />
    </div>

    <!-- Create Modal -->
    <woot-modal
      v-model:show="showCreateModal"
      :on-close="closeCreateModal"
      size="large"
    >
      <div class="flex flex-col h-[80vh] md:h-auto">
        <woot-modal-header
          :header-title="
            $t('INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.CREATE.TITLE')
          "
        />
        <HSMTemplateForm
          v-model:name="name"
          v-model:category="category"
          v-model:language="language"
          v-model:header="header"
          v-model:footer="footer"
          v-model:media-type="mediaType"
          v-model:body="body"
          v-model:buttons="buttons"
          v-model:body-variable-examples="bodyVariableExamples"
          :detected-variables="detectedVariables"
          :category-options="categoryOptions"
          :language-options="languageOptions"
          :is-loading="uiFlags.isCreatingTemplate"
          :is-form-valid="isFormValid"
          :preview-template="previewTemplate"
          :is-edit-mode="false"
          :name-error="v$.name.$error"
          :body-error="v$.body.$error"
          @submit="createTemplate"
          @cancel="closeCreateModal"
          @add-button="addButton"
          @remove-button="removeButton"
          @update:file="file = $event"
        />
      </div>
    </woot-modal>

    <!-- Edit Modal -->
    <woot-modal
      v-model:show="showEditModal"
      :on-close="closeEditModal"
      size="large"
    >
      <div class="flex flex-col h-[80vh] md:h-auto">
        <woot-modal-header
          :header-title="
            $t('INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.EDIT.TITLE')
          "
        />
        <HSMTemplateForm
          v-model:name="name"
          v-model:category="category"
          v-model:language="language"
          v-model:header="header"
          v-model:footer="footer"
          v-model:media-type="mediaType"
          v-model:body="body"
          v-model:buttons="buttons"
          v-model:body-variable-examples="bodyVariableExamples"
          :detected-variables="detectedVariables"
          :category-options="categoryOptions"
          :language-options="languageOptions"
          :is-loading="uiFlags.isCreatingTemplate"
          :is-form-valid="isFormValid"
          :preview-template="previewTemplate"
          is-edit-mode
          :name-error="v$.name.$error"
          :body-error="v$.body.$error"
          @submit="updateTemplate"
          @cancel="closeEditModal"
          @add-button="addButton"
          @remove-button="removeButton"
        />
      </div>
    </woot-modal>

    <!-- Delete Confirmation Modal -->
    <woot-delete-modal
      v-model:show="showDeleteConfirm"
      :on-close="cancelDelete"
      :on-confirm="deleteTemplate"
      :title="$t('INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.DELETE.TITLE')"
      :message="deleteConfirmMessage"
      :confirm-text="
        $t('INBOX_MGMT.SETTINGS_POPUP.HSM_TEMPLATES.DELETE.SUBMIT')
      "
      :reject-text="$t('INBOX_MGMT.DELETE.CONFIRM.NO')"
      :is-loading="uiFlags.isDeletingTemplate"
    />
  </div>
</template>
